/**
 * campaignGreeks — shared "is this Greek working FOR or AGAINST the position?" verdicts for the
 * staged-campaign UI (dialog + monitor + adjustment panel). Colours a Θ/Δ/Vega box green (in your
 * favour), red (against) or neutral, and returns a plain-English tooltip, RELATIVE to the view.
 *
 *  • Theta: positive = time decay earns you money (good); negative = it bleeds you (bad).
 *  • Delta: good when the directional lean matches the view (range wants ~0).
 *  • Vega : premium-selling views (won't fall/rise, stay-in-range) want NEGATIVE vega (IV falling);
 *           breakout / premium-buying views want POSITIVE vega (IV rising).
 */
export interface GreekVerdict { color?: string; favour: 'good' | 'bad' | 'neutral'; note: string; }

export const GREEK_GREEN = '#2e7d32';
export const GREEK_RED = '#c62828';
export const GREEK_AMBER = '#ed6c02';

const isShortVol = (v: string) => v === 'WONT_FALL_BELOW' || v === 'WONT_RISE_ABOVE' || v === 'STAY_IN_RANGE';
const wantsUp = (v: string) => v === 'WONT_FALL_BELOW' || v === 'WILL_BREAK_ABOVE';
const wantsDown = (v: string) => v === 'WONT_RISE_ABOVE' || v === 'WILL_BREAK_BELOW';
const isRange = (v: string) => v === 'STAY_IN_RANGE';
const isBreach = (v: string) => v === 'WILL_BREACH';

export function thetaVerdict(theta: number): GreekVerdict {
  if (theta > 1) return { color: GREEK_GREEN, favour: 'good', note: 'Positive theta — time decay works FOR you: the position earns money for every day price sits still.' };
  if (theta < -1) return { color: GREEK_RED, favour: 'bad', note: 'Negative theta — time decay works AGAINST you: you lose a little value each day, so your move needs to arrive soon.' };
  return { favour: 'neutral', note: 'Theta is roughly flat — time decay is a non-factor right now.' };
}

export function deltaVerdict(view: string, delta: number): GreekVerdict {
  if (isRange(view)) {
    if (Math.abs(delta) < 0.15) return { color: GREEK_GREEN, favour: 'good', note: 'Delta is near zero — direction-neutral, exactly what a range trade wants.' };
    const dir = delta > 0 ? 'up' : 'down';
    return { color: GREEK_AMBER, favour: 'bad', note: `Delta is skewed ${dir} (${delta.toFixed(2)}) — a range trade prefers ~0; price drifting ${dir} works against it.` };
  }
  if (isBreach(view)) return { favour: 'neutral', note: 'A break-either-way trade is delta-agnostic — it profits from a big move in either direction.' };
  if (Math.abs(delta) < 0.05) return { favour: 'neutral', note: 'Delta is small — little directional lean either way.' };
  const aligned = (wantsUp(view) && delta > 0) || (wantsDown(view) && delta < 0);
  return aligned
    ? { color: GREEK_GREEN, favour: 'good', note: `Delta leans ${delta > 0 ? 'up' : 'down'} (${delta.toFixed(2)}) — aligned with your view, working FOR you.` }
    : { color: GREEK_RED, favour: 'bad', note: `Delta leans ${delta > 0 ? 'up' : 'down'} (${delta.toFixed(2)}) — opposite your view, working AGAINST you.` };
}

export function vegaVerdict(view: string, vega: number): GreekVerdict {
  const shortVol = isShortVol(view);
  if (Math.abs(vega) < 1) return { favour: 'neutral', note: 'Vega is small — a change in implied volatility barely moves this position.' };
  const favourable = shortVol ? vega < 0 : vega > 0;
  if (favourable) return { color: GREEK_GREEN, favour: 'good', note: shortVol ? 'Negative vega — you profit if implied volatility FALLS, which suits this premium-selling view.' : 'Positive vega — you profit if implied volatility RISES, which suits a breakout / premium-buying view.' };
  return { color: GREEK_RED, favour: 'bad', note: shortVol ? 'Positive vega — a RISE in implied volatility hurts this premium-selling view.' : 'Negative vega — a FALL in implied volatility hurts a breakout / premium-buying view.' };
}
